// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — the shape of a value-encoded DASH release's `valueEncoding`,
 * as `services/dashRelease.ts` parses it. Kept in `types/` so
 * `Dataset.releaseEncoding` can name it without the types folder
 * importing service code (which the Pages Functions and CLI builds,
 * sharing `types/`, cannot compile).
 */

import type { ColorScaleStop } from './color-scale'

export type ReleaseEncodingKind = 'luma8-linear' | 'luma8-log' | 'luma8-classified'

export interface ReleaseClass {
  readonly code: number
  readonly value: number
  readonly label: string
}

/** AYNI: one class of an auxiliary map, e.g. IMERG's "snow", with its own palette when it has one. */
export interface ReleaseAuxClass {
  readonly code: number
  readonly value: number
  readonly label: string
  /** Its palette, on the same palette axis as the default one; null: drawn with the default palette. */
  readonly stops: readonly ColorScaleStop[] | null
}

/**
 * AYNI: a classified map in another rectangle of the same frame
 * (`valueEncoding.auxiliary`): IMERG's "phase" says whether each pixel's
 * precipitation is rain or snow. Ported from AYNI 2D (`value-encoding.ts`,
 * `luma-auxiliary.ts`).
 */
export interface ReleaseAuxiliary {
  readonly name: string
  /** Rows and columns within the frame, from the top-left, like `dataRegion`. */
  readonly region: { readonly x: number; readonly y: number; readonly width: number; readonly height: number }
  readonly classes: readonly ReleaseAuxClass[]
  /** The class a code below `nodataThresholdCode` stands for (IMERG: rain), or null for none. */
  readonly defaultClass: number | null
}

/** The parts of `release.json`'s `valueEncoding` the VR globe needs. */
export interface ReleaseEncoding {
  readonly kind: ReleaseEncodingKind
  readonly units: string | null
  readonly vmin: number
  readonly vmax: number
  readonly nodataThresholdCode: number
  readonly dataMinCode: number
  readonly dataMaxCode: number
  /** Map rows within the frame, in pixels from the top-left. */
  readonly dataRegion: { readonly x: number; readonly y: number; readonly width: number; readonly height: number }
  readonly frameWidth: number
  readonly frameHeight: number
  /** Geographic extent of the data region. */
  readonly geo: { readonly latTop: number; readonly latBottom: number; readonly lonLeft: number; readonly lonRight: number }
  readonly stops: readonly ColorScaleStop[]
  readonly alphaMode: 'gradient' | 'binary' | 'opaque'
  readonly transparentBelowValue: number | null
  readonly alphaGradient: { readonly rampStartValue: number; readonly rampEndValue: number; readonly gamma: number } | null
  readonly classes: readonly ReleaseClass[]
  /** AYNI: classified maps elsewhere in the frame (IMERG's rain/snow phase); empty for most releases. */
  readonly auxiliary?: readonly ReleaseAuxiliary[]
  /**
   * AYNI: the release's measured error budget and tolerance
   * (`release.json` → `errorBudget`, `tolerance`, `exact`; own data now,
   * public streams once the stream host adds them, O9). Absent: only the
   * 8-bit step is known.
   */
  readonly accuracy?: import('./error-budget').ReleaseAccuracy
  /**
   * AYNI: the calibration strip (`valueEncoding.calibration`): the frame
   * row to sample, from the top, and the source code of each of its equal
   * bands, left to right. `services/decoderRange.ts` reads it to see what
   * the browser's decoder did to the codes. Absent when the release has none.
   */
  readonly calibration?: { readonly row: number; readonly codes: readonly number[] }
  /**
   * AYNI: set on an encoding `services/decoderRange.ts` moved into a
   * browser decoder's code space: the encoding as published and the line
   * the decoder applied (`gain × source + offset`). A decoded code is read
   * by snapping it back to the source code it stands for (value error
   * audit, 2026-10-19: decoding the fractional code left up to half a
   * code of error; snapping recovered every pixel exactly).
   */
  readonly decodedFrom?: { readonly source: ReleaseEncoding; readonly gain: number; readonly offset: number }
  /**
   * AYNI: what the release says about its codec error, clipping,
   * regridding and source, and where each frame's custody entry is
   * (`release-fidelity.ts`; value error audit, 2026-10-19). Absent for an
   * encoding read without its release.json (own data's job page, tests).
   */
  readonly fidelity?: import('./release-fidelity').ReleaseFidelity
}

// ---------------------------------------------------------------------------
// Decoding (pure, so `types/color-scale.ts` can decode a release exactly)
// ---------------------------------------------------------------------------

/** The class nearest a code: a lossy codec can move a class code by a step or two. */
function nearestClass(enc: ReleaseEncoding, code: number): ReleaseClass {
  let nearest = enc.classes[0]!
  for (const c of enc.classes) if (Math.abs(c.code - code) < Math.abs(nearest.code - code)) nearest = c
  return nearest
}

/** Palette position of a code: 0 at `dataMinCode`, 1 at `dataMaxCode`, clamped. */
export function paletteT(enc: ReleaseEncoding, code: number): number {
  // A classified frame carries a handful of exact codes, so snap to the
  // nearest class instead of blending two categories into a colour neither has.
  if (enc.kind === 'luma8-classified' && enc.classes.length > 0) code = nearestClass(enc, code).code
  const t = (code - enc.dataMinCode) / (enc.dataMaxCode - enc.dataMinCode)
  return Math.min(1, Math.max(0, t))
}

/** The value at a palette position (0 = `vmin`, 1 = `vmax`): linear, or logarithmic for `luma8-log`. */
export function valueAtPaletteT(enc: ReleaseEncoding, t: number): number {
  if (enc.kind === 'luma8-log') return enc.vmin * Math.pow(enc.vmax / enc.vmin, t)
  return enc.vmin + t * (enc.vmax - enc.vmin)
}

/** The palette position of a value, the inverse of {@link valueAtPaletteT}; not clamped. */
export function paletteTOfValue(enc: ReleaseEncoding, value: number): number {
  if (enc.kind === 'luma8-log') {
    if (!(value > 0)) return 0
    return Math.log(value / enc.vmin) / Math.log(enc.vmax / enc.vmin)
  }
  return (value - enc.vmin) / (enc.vmax - enc.vmin)
}

/**
 * The publisher's code a decoded code stands for: the nearest code on the
 * source lattice. A decoder that spreads 219 source codes over 256 maps
 * distinct codes to distinct codes, so rounding the inverse recovers each
 * exactly. Unchanged for an encoding that was never moved.
 */
export function sourceCodeAt(enc: ReleaseEncoding, code: number): number {
  const d = enc.decodedFrom
  if (!d) return code
  return Math.min(255, Math.max(0, Math.round((code - d.offset) / d.gain)))
}

/** The measured value a code stands for, or null for no data. */
export function valueAtCode(enc: ReleaseEncoding, code: number): number | null {
  if (enc.decodedFrom) return valueAtCode(enc.decodedFrom.source, sourceCodeAt(enc, code))
  if (code < enc.nodataThresholdCode) return null
  if (enc.kind === 'luma8-classified' && enc.classes.length > 0) return nearestClass(enc, code).value
  return valueAtPaletteT(enc, paletteT(enc, code))
}
